import { useState, useCallback } from 'react';
import { Icon, Page, PageHeader, RowActions, Table, clickableRow } from '../components/primitives';
import { Link, useNavigate } from 'react-router-dom';
import { api, serverInUseDetails, type Server, type ServerHosting } from '../api';
import { LevelBadge } from '../components/project/ui';
import { agentStateLabel } from '../components/project/operationalHealth';
import { HostedEnvironments } from '../components/portfolio/HostedEnvironments';
import { useQuery } from '../hooks';
import { queries } from '../queries';
import { ConfirmDialog, EmptyState, ErrorNotice, ServerInUseNotice, SkeletonPage, StatusBadge } from '../components/ui';

/** What removing a server really does — shown verbatim in the confirm
 * dialog here and on the server page. Placements, onboarding plans,
 * deployment history and monitors of environments on this server are
 * deleted with it (database cascade); the server itself is untouched. */
export const serverRemovalConsequences =
  'The server record is deleted and its agent is disconnected. Commerce Ops refuses the removal while an environment is still placed on this server or a run on it has not finished; move or remove those environments first. Finished runs, deployment history and monitors recorded for this server are deleted with it. Nothing on the server itself is changed — to remove the agent from it, run "commerceops-agent uninstall --purge" there. This cannot be undone.';

function formatRelativeTime(isoString?: string): string {
  if (!isoString) return 'Never';
  const date = new Date(isoString);
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSec < 10) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return date.toLocaleDateString();
}

export function ServersPage() {
  const navigate = useNavigate();
  const [reloadToken, setReloadToken] = useState(0);
  const [serverToDelete, setServerToDelete] = useState<Server | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<Error | null>(null);

  const servers = useQuery(queries.serverRows(), [reloadToken]);
  // What each server hosts and its agent state as the server derives it
  // (GET /operational-health, CO-96): the stored status alone says
  // "online" for an agent that has been silent for days.
  const health = useQuery(queries.organizationHealth(), [reloadToken]);
  const hosting = new Map<string, ServerHosting>((health.data?.servers ?? []).map((entry) => [entry.server_id, entry]));

  // Live connection and audit changes (server.* and readiness.* events) refresh
  // 'servers:rows' and 'org-health' through the shared events client
  // (useLiveRefresh, mounted once by the dashboard).

  const handleDelete = useCallback(async () => {
    if (!serverToDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteServer(serverToDelete.id);
      setServerToDelete(null);
      setReloadToken((t) => t + 1);
    } catch (err) {
      // Close the dialog so the refusal (and the environments that caused
      // it) is readable; nothing was changed.
      setServerToDelete(null);
      setDeleteError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsDeleting(false);
    }
  }, [serverToDelete]);

  return (
    <Page>
      <PageHeader
        title="Servers"
        actions={
          <Link to="/app/servers/new" className="button button--primary">
            <Icon name="plus" />
            Connect server
          </Link>
        }
      />

      {servers.loading ? (
        <SkeletonPage />
      ) : servers.error ? (
        <ErrorNotice error={servers.error} />
      ) : !servers.data || servers.data.length === 0 ? (
        <EmptyState title="No servers" description="">
          <Link to="/app/servers/new" className="button button--primary">
            Connect server
          </Link>
        </EmptyState>
      ) : (
        <Table label="Servers">
          <thead>
            <tr>
              <th scope="col">Server</th>
              <th scope="col">Agent</th>
              <th scope="col">Hosts</th>
              <th scope="col">Server check</th>
              <th scope="col">Operating system</th>
              <th scope="col">Agent version</th>
              <th scope="col">Last seen</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {servers.data.map((srv) => (
              <tr key={srv.id} {...clickableRow(() => navigate(`/app/servers/${srv.id}`))}>
                <th scope="row">
                  <Link to={`/app/servers/${srv.id}`} className="table__primary-link" onClick={(e) => e.stopPropagation()}>
                    {srv.name}
                  </Link>
                  <div className="table__sub font-mono">{srv.hostname || 'No hostname reported'}</div>
                </th>
                <td>
                  <AgentCell server={srv} hosting={hosting.get(srv.id)} />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  {hosting.get(srv.id) ? (
                    <HostedEnvironments environments={hosting.get(srv.id)!.environments} limit={3} />
                  ) : (
                    <span className="muted">{health.loading ? 'Reading…' : '—'}</span>
                  )}
                </td>
                <td>
                  <StatusBadge kind="serverCheck" value={srv.readiness_status} />
                </td>
                <td>{srv.os ? `${srv.os}${srv.architecture ? ` (${srv.architecture})` : ''}` : <span className="muted">—</span>}</td>
                <td className="font-mono">{srv.agent_version ? `v${srv.agent_version}` : <span className="muted">—</span>}</td>
                <td className="muted">{formatRelativeTime(srv.last_seen_at)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <RowActions>
                    <button
                      type="button"
                      className="button button--danger-quiet button--small"
                      onClick={() => setServerToDelete(srv)}
                      aria-label={`Remove ${srv.name}`}
                    >
                      Remove
                    </button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {serverToDelete && (
        <ConfirmDialog
          open={true}
          title={`Remove ${serverToDelete.name} from Commerce Ops?`}
          description={serverRemovalConsequences}
          confirmLabel="Remove server"
          confirmText={serverToDelete.name}
          danger={true}
          busy={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setServerToDelete(null)}
        />
      )}

      {deleteError && (
        <div className="page-footnote">
          {serverInUseDetails(deleteError) ? <ServerInUseNotice error={deleteError} /> : <ErrorNotice error={deleteError} />}
        </div>
      )}
    </Page>
  );
}

/** The agent's derived state; the stored status only while it loads. */
function AgentCell({ server, hosting }: { server: Server; hosting?: ServerHosting }) {
  if (!hosting) return <StatusBadge kind="serverAgent" value={server.status} />;
  return (
    <span title={hosting.agent.fact}>
      <LevelBadge level={hosting.agent.level}>{agentStateLabel[hosting.agent.state] ?? hosting.agent.state}</LevelBadge>
    </span>
  );
}
